import React, {useEffect, useMemo, useState} from "react";
import NiceModal from "@ebay/nice-modal-react";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {restrictToVerticalAxis} from "@dnd-kit/modifiers";
import {SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy} from "@dnd-kit/sortable";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {zodResolver} from "@hookform/resolvers/zod";
import {Controller, useFieldArray, useForm} from "react-hook-form";
import {useNavigate, useParams} from "react-router-dom";
import {toast} from "sonner";
import AreYouSureDialog from "@bounce/shared/consumer/bounce-ui/components/Dialogs/AreYouSureDialog";
import {Box, Card, Colors, Container, IconButton, IconTrash, Text, Tooltip} from "@bounce/shared/ui";
import {Button, MenuItem, Select, TextField} from "../../ui/components";
import {useCreateTrigger} from "../../apis/requests/triggers/createTrigger";
import {useGetTriggers} from "../../apis/requests/triggers/getTriggers";
import {useGetTriggerSchema} from "../../apis/requests/triggers/getTriggerSchema";
import {ReachTrigger, TriggerCondition, TriggerInteractionType} from "../../apis/requests/triggers/types";
import {useUpdateTrigger} from "../../apis/requests/triggers/updateTrigger";
import {reachUrls} from "../../layout/menu-items";
import ConditionCard from "./ConditionCard";
import ConditionDialog, {ConditionDialogResult} from "./ConditionDialog";
import {makeRule, triggerUrl} from "./helpers";
import {InteractionChip} from "./TriggerBadges";
import {TRIGGER_STATUS_DISPLAY} from "./triggerDisplay";
import {
  DEFAULT_CONDITION,
  EMPTY_CONDITION,
  EMPTY_TRIGGER,
  TRIGGER_INTERACTION_TYPES,
  TRIGGER_STATUSES,
  TriggerFormValues,
  toFormValues,
  toTriggerPayload,
  triggerFormSchema,
} from "./triggerFormSchema";
import TriggerTestPanel from "./TriggerTestPanel";
import {FieldLabel, mutedButtonSx} from "./triggerUi";
import {useConfirmDeleteTrigger} from "./useConfirmDeleteTrigger";
import {useTriggerTemplates} from "./useTriggerTemplates";

const TriggerFormPage = () => {
  const navigate = useNavigate();
  const {id} = useParams<{id?: string}>();
  const triggerId = id === undefined ? undefined : Number(id);
  const isEdit = triggerId !== undefined;

  const {data: triggers = [], isLoading, error} = useGetTriggers();
  const existing = isEdit ? triggers.find((trigger) => trigger.id === triggerId) : undefined;
  const {data: schema} = useGetTriggerSchema();
  const {channels} = useTriggerTemplates();
  const {mutateAsync: createAsync, isPending: isCreating} = useCreateTrigger();
  const {mutateAsync: updateAsync, isPending: isUpdating} = useUpdateTrigger();
  const {confirmDelete, isDeleting} = useConfirmDeleteTrigger();
  const isBusy = isCreating || isUpdating || isDeleting;

  const [savedTrigger, setSavedTrigger] = useState<ReachTrigger | null>(null);
  const {control, handleSubmit, reset, watch, getValues, formState} = useForm<TriggerFormValues>({
    resolver: zodResolver(triggerFormSchema),
    defaultValues: EMPTY_TRIGGER,
  });
  const {fields, move, remove, update, replace} = useFieldArray({control, name: "conditions"});

  useEffect(() => {
    if (!existing || savedTrigger?.id === existing.id) return;
    setSavedTrigger(existing);
    reset(toFormValues(existing));
  }, [existing, savedTrigger, reset]);

  useEffect(() => {
    if (!formState.isDirty) return;
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [formState.isDirty]);

  const interactionType = watch("interaction_type");
  const conditions = watch("conditions");
  const interactionSchema = schema?.interaction_types.find((t) => t.interaction_type === interactionType);
  const ordered = conditions.filter((c) => !c.is_default);
  const defaultCondition = conditions.find((c) => c.is_default) ?? DEFAULT_CONDITION;
  const orderedIds = fields.slice(0, ordered.length).map((field) => field.id);
  const parameters = useMemo(() => interactionSchema?.parameters ?? [], [interactionSchema]);

  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: 8}}),
    useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
  );

  const movedLocked = (next: TriggerCondition[]) => ordered.find((c, i) => c.locked && next[i] !== c);
  const placeAt = (others: TriggerCondition[], condition: TriggerCondition, priority: number) => {
    const next = [...others];
    next.splice(priority - 1, 0, condition);
    return next;
  };

  const onDragEnd = ({active, over}: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = orderedIds.indexOf(String(active.id));
    const to = orderedIds.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    const locked = movedLocked(arrayMove(ordered, from, to));
    if (locked) {
      toast.error(`"${locked.name}" is locked and keeps its place. Unlock it to change the order.`);
      return;
    }
    move(from, to);
  };

  const deleteCondition = (index: number) =>
    NiceModal.show(AreYouSureDialog, {
      onConfirm: () => remove(index),
      headerTextId: "Delete condition?",
      contentTextId: `Are you sure you want to delete "${ordered[index].name}"?`,
      confirmTextId: "Delete",
    });

  const unlockCondition = (index: number) =>
    NiceModal.show(AreYouSureDialog, {
      onConfirm: () => update(index, {...ordered[index], locked: false}),
      headerTextId: "Unlock condition?",
      contentTextId: `"${ordered[index].name}" is locked so it isn't changed by mistake. Unlock it to edit, move or delete it.`,
      confirmTextId: "Unlock",
    });

  const openCondition = (index: number | null) => {
    const isNew = index === null;
    const isDefault = index === ordered.length;
    const condition = isNew
      ? {...EMPTY_CONDITION, rules: [makeRule(parameters)]}
      : isDefault
      ? defaultCondition
      : ordered[index];
    const others = isNew || isDefault ? ordered : ordered.filter((_, i) => i !== index);
    const priorityCount = isNew ? ordered.length + 1 : ordered.length;
    const lockedPriorities = isDefault
      ? []
      : Array.from({length: priorityCount}, (_, i) => i + 1).filter(
          (priority) => !!movedLocked(placeAt(others, condition, priority))
        );
    const nextConditions = (result: ConditionDialogResult): TriggerCondition[] => {
      if (isDefault) return result.action === "save" ? [...ordered, result.condition] : conditions;
      if (result.action === "delete") return [...others, defaultCondition];
      return [...placeAt(others, result.condition, result.priority), defaultCondition];
    };
    NiceModal.show(ConditionDialog, {
      condition,
      isNew,
      priority: isNew ? ordered.length + 1 : index + 1,
      priorityCount,
      lockedPriorities,
      parameters,
      interactionType,
      onSubmit: (result: ConditionDialogResult) => replace(nextConditions(result)),
    });
  };

  const onSave = handleSubmit(async (values) => {
    const payload = toTriggerPayload(values);
    try {
      if (savedTrigger) {
        const saved = await updateAsync({id: savedTrigger.id, ...payload});
        setSavedTrigger(saved);
        reset(toFormValues(saved));
        toast.success(`Trigger "${saved.name}" saved`);
      } else {
        const created = await createAsync(payload);
        toast.success(`Trigger "${created.name}" created`);
        navigate(triggerUrl(created.id), {replace: true});
      }
    } catch {}
  });

  const onDelete = () => {
    if (existing) confirmDelete(existing, () => navigate(reachUrls.triggers));
  };

  if (isEdit && !isLoading && (error || !existing)) {
    return (
      <Box sx={{p: 4}}>
        <Text size={14}>Trigger not found.</Text>
      </Box>
    );
  }

  return (
    <Box sx={{p: 4, display: "flex", flexDirection: "column", gap: 2}}>
      <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
        <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
          <IconButton onClick={() => navigate(reachUrls.triggers)} aria-label="Back to triggers">
            <ArrowBackIcon sx={{width: 20, height: 20, color: Colors.grey._700}} />
          </IconButton>
          <Text size={24} variant="interDisplay600">
            {isEdit ? "Edit trigger" : "New trigger"}
          </Text>
        </Box>
        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
          {isEdit && (
            <Tooltip title="Delete trigger">
              <IconButton
                onClick={onDelete}
                disabled={isBusy}
                sx={{
                  border: `1px solid ${Colors.red._300}`,
                  borderRadius: "8px",
                  padding: "6px",
                  color: Colors.red._600,
                  "& svg path": {stroke: "currentColor"},
                  "&:hover": {backgroundColor: Colors.red._100, color: Colors.red._800},
                }}
              >
                <Box component={IconTrash} sx={{width: 20, height: 20}} />
              </IconButton>
            </Tooltip>
          )}
          {formState.isDirty && (
            <Text size={13} sx={{color: Colors.grey._600, mx: 0.5}}>
              Unsaved changes
            </Text>
          )}
          <Button
            buttonType="outlined-primary"
            size="small"
            onClick={() => reset()}
            disabled={isBusy || !formState.isDirty}
            sx={mutedButtonSx}
          >
            Cancel
          </Button>
          <Button
            buttonType="contained-primary"
            size="small"
            onClick={onSave}
            disabled={isBusy || (isEdit && !formState.isDirty)}
          >
            {isEdit ? "Save trigger" : "Create trigger"}
          </Button>
        </Box>
      </Box>

      <Box sx={{display: "flex", gap: 2.5, alignItems: "flex-start"}}>
        <Box sx={{flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2}}>
          <Card sx={{p: 3, display: "flex", flexDirection: "column", alignItems: "stretch", gap: 2}}>
            <Box>
              <FieldLabel>Name *</FieldLabel>
              <Controller
                name="name"
                control={control}
                render={({field: {ref, ...field}, fieldState}) => (
                  <TextField
                    {...field}
                    inputRef={ref}
                    fullWidth
                    size="small"
                    placeholder="Failed payment"
                    error={!!fieldState.error}
                  />
                )}
              />
            </Box>
            <Container row fullWidth sx={{gap: 1.5, alignItems: "flex-start", flexWrap: "nowrap"}}>
              <Box sx={{width: 200, flexShrink: 0}}>
                <FieldLabel>Status *</FieldLabel>
                <Controller
                  name="status"
                  control={control}
                  render={({field}) => (
                    <Select {...field} fullWidth size="small">
                      {TRIGGER_STATUSES.map((status) => (
                        <MenuItem key={status} value={status}>
                          {TRIGGER_STATUS_DISPLAY[status].label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Box>
              <Box sx={{flex: 1, minWidth: 0}}>
                <FieldLabel>Description</FieldLabel>
                <Controller
                  name="description"
                  control={control}
                  render={({field: {ref, ...field}}) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      fullWidth
                      size="small"
                      placeholder="Dunning after a declined charge"
                    />
                  )}
                />
              </Box>
            </Container>
            <Box>
              <FieldLabel>Interaction *</FieldLabel>
              <Controller
                name="interaction_type"
                control={control}
                render={({field: {onChange, ...field}}) => (
                  <Select
                    {...field}
                    size="small"
                    sx={{width: 320}}
                    disabled={isEdit}
                    renderValue={(value) => <InteractionChip interactionType={value as TriggerInteractionType} />}
                    onChange={(e) => {
                      onChange(e);
                      replace([{...defaultCondition, rules: []}]);
                    }}
                  >
                    {TRIGGER_INTERACTION_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        <InteractionChip interactionType={type} />
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <Text size={12} sx={{color: Colors.grey._600, mt: 0.5}}>
                {isEdit
                  ? "The interaction can't change after the trigger is created."
                  : "Changing the interaction clears the conditions, because each interaction has its own fields."}
              </Text>
            </Box>
          </Card>

          <Card sx={{p: 3, display: "flex", flexDirection: "column", alignItems: "stretch", gap: 1.5}}>
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
              <Box>
                <Text size={16} variant="inter600">
                  Conditions
                </Text>
                <Text size={12} sx={{color: Colors.grey._600}}>
                  Checked top to bottom. The first one that matches decides what is sent. Drag to change the order;
                  locked conditions keep their place.
                </Text>
              </Box>
              <Button buttonType="outlined-primary" size="small" onClick={() => openCondition(null)}>
                + Add condition
              </Button>
            </Box>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={onDragEnd}
            >
              <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
                <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
                  {ordered.map((condition, index) => (
                    <ConditionCard
                      key={orderedIds[index]}
                      id={orderedIds[index]}
                      priority={index + 1}
                      condition={condition}
                      channels={channels}
                      onOpen={() => (condition.locked ? unlockCondition(index) : openCondition(index))}
                      onDelete={() => deleteCondition(index)}
                    />
                  ))}
                  <ConditionCard
                    id="condition-default"
                    priority={ordered.length + 1}
                    condition={defaultCondition}
                    channels={channels}
                    onOpen={() => openCondition(ordered.length)}
                  />
                </Box>
              </SortableContext>
            </DndContext>
          </Card>
        </Box>
        <TriggerTestPanel
          interactionType={interactionType}
          conditionCount={conditions.length}
          conditionsKey={JSON.stringify(conditions)}
          getTrigger={() => {
            const payload = toTriggerPayload(getValues());
            return {...payload, name: payload.name || "Untitled trigger"};
          }}
        />
      </Box>
    </Box>
  );
};

export default TriggerFormPage;
